import React from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import AppBar from '../../components/AppBar'
import LogoutContainer from '../../components/auth/LogoutContainer'
import { clearInfo } from '../../storage'
import {
  getUserTimezone,
  getBrowserTimezone,
  saveUserTimezone,
  clearUserTimezone,
  COMMON_TIMEZONES,
} from '../../utils/timezoneStorage'
import { DateTime } from 'luxon'

class AccountSettingsScene extends React.Component {
  state = {
    selectedTimezone: getUserTimezone(),
    currentTime: DateTime.now().setZone(getUserTimezone()).toISO(),
    selectedMenu: 'user-info', // 'user-info' or 'timezone'
  }

  componentDidMount() {
    // Update current time every second
    this.timeInterval = setInterval(() => {
      this.setState({
        currentTime: DateTime.now()
          .setZone(this.state.selectedTimezone)
          .toISO(),
      })
    }, 1000)
  }

  componentWillUnmount() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval)
    }
  }

  handleMenuSelect = (menu) => {
    this.setState({ selectedMenu: menu })
  }

  handleTimezoneChange = (event) => {
    const selectedTimezone = event.target.value
    saveUserTimezone(selectedTimezone)
    this.setState({
      selectedTimezone,
      currentTime: DateTime.now().setZone(selectedTimezone).toISO(),
    })
  }

  handleResetTimezone = () => {
    const browserTimezone = getBrowserTimezone()
    clearUserTimezone()
    this.setState({
      selectedTimezone: browserTimezone,
      currentTime: DateTime.now().setZone(browserTimezone).toISO(),
    })
    alert('Timezone reset to browser default: ' + browserTimezone)
  }

  handleLogout = () => {
    clearInfo()
    this.props.history.push('/')
  }

  handleBack = () => {
    this.props.history.goBack()
  }

  getCurrentTimeFormatted = () => {
    const dt = DateTime.fromISO(this.state.currentTime)
    return dt.toFormat('yyyy-MM-dd HH:mm:ss ZZZZ')
  }

  renderUserInfo() {
    const { auth } = this.props

    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          User Information
        </h2>

        <div className="grid grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-gray-500 text-sm font-semibold uppercase tracking-wide mb-2">
              Name
            </label>
            <div className="text-lg font-semibold text-gray-900">
              {auth && auth.user ? auth.user.name : 'N/A'}
            </div>
          </div>

          <div>
            <label className="block text-gray-500 text-sm font-semibold uppercase tracking-wide mb-2">
              Role
            </label>
            <div className="flex flex-wrap gap-2">
              {auth && auth.user && auth.user.roles
                ? auth.user.roles.map((role, idx) => (
                    <span
                      key={idx}
                      className="inline-block px-3 py-1 rounded-full text-sm font-semibold text-white bg-primary"
                    >
                      {role}
                    </span>
                  ))
                : 'N/A'}
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-gray-200">
          <LogoutContainer onLogout={this.handleLogout}>
            <button className="bg-gradient-to-br from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white font-semibold py-2 px-6 rounded-lg transition-all shadow-md hover:shadow-lg">
              <span className="inline-block mr-2">🚪</span>
              Logout
            </button>
          </LogoutContainer>
        </div>
      </div>
    )
  }

  renderTimezoneSettings() {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Timezone Settings
        </h2>

        <div className="mb-6">
          <label className="block text-gray-500 text-sm font-semibold uppercase tracking-wide mb-2">
            Select Timezone
          </label>
          <select
            value={this.state.selectedTimezone}
            onChange={this.handleTimezoneChange}
            className="w-full px-3 py-2 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>

        <div className="pt-6 border-t border-gray-200">
          <button
            onClick={this.handleResetTimezone}
            className="bg-white text-primary font-semibold py-2 px-6 rounded-lg transition-all hover:shadow-md border-2 border-primary hover:bg-primary-50"
          >
            Reset to Browser
          </button>
        </div>
      </div>
    )
  }

  render() {
    return (
      <div className="h-svh">
        <AppBar header="ACCOUNT SETTINGS" />

        <div className="flex h-full pt-20">
          {/* Left Sidebar Menu */}
          <div className="flex flex-col bg-gray-50 border-r border-gray-200 w-24 md:w-60">
            <div className="p-4">
              <h3 className="text-lg font-bold text-gray-700 mb-4">Settings</h3>

              <div className="space-y-2">
                <button
                  onClick={() => this.handleMenuSelect('user-info')}
                  className={`w-full flex items-center justify-center md:justify-start text-left px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                    this.state.selectedMenu === 'user-info'
                      ? 'text-white shadow-md bg-gradient-to-br from-primary to-primary-600'
                      : 'text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  <span className="md:mr-2 flex">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                  <span className="hidden md:inline">User Information</span>
                </button>

                <button
                  onClick={() => this.handleMenuSelect('timezone')}
                  className={`w-full flex items-center justify-center md:justify-start text-left px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                    this.state.selectedMenu === 'timezone'
                      ? 'text-white shadow-md bg-gradient-to-br from-primary to-primary-600'
                      : 'text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  <span className="md:mr-2 flex">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path d="M15.75 8.25a.75.75 0 0 1 .75.75c0 1.12-.492 2.126-1.27 2.812a.75.75 0 1 1-.992-1.124A2.243 2.243 0 0 0 15 9a.75.75 0 0 1 .75-.75Z" />
                      <path
                        fillRule="evenodd"
                        d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM4.575 15.6a8.25 8.25 0 0 0 9.348 4.425 1.966 1.966 0 0 0-1.84-1.275.983.983 0 0 1-.97-.822l-.073-.437c-.094-.565.25-1.11.8-1.267l.99-.282c.427-.123.783-.418.982-.816l.036-.073a1.453 1.453 0 0 1 2.328-.377L16.5 15h.628a2.25 2.25 0 0 1 1.983 1.186 8.25 8.25 0 0 0-6.345-12.4c.044.262.18.503.389.676l1.068.89c.442.369.535 1.01.216 1.49l-.51.766a2.25 2.25 0 0 1-1.161.886l-.143.048a1.107 1.107 0 0 0-.57 1.664c.369.555.169 1.307-.427 1.605L9 13.125l.423 1.059a.956.956 0 0 1-1.652.928l-.679-.906a1.125 1.125 0 0 0-1.906.172L4.575 15.6Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                  <span className="hidden md:inline">Timezone Settings</span>
                </button>
              </div>
            </div>

            <div className="mt-auto p-4">
              <button
                onClick={this.handleBack}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-all shadow-md hover:shadow-lg"
              >
                ← Back
              </button>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 bg-white overflow-y-auto">
            {this.state.selectedMenu === 'user-info' && this.renderUserInfo()}
            {this.state.selectedMenu === 'timezone' &&
              this.renderTimezoneSettings()}
          </div>
        </div>
      </div>
    )
  }
}

export default withRouter(
  connect(({ auth }) => ({ auth }))(AccountSettingsScene),
)
